import { AuthContext } from "@/hooks/auth/AuthContext";
import { clearBuildingSetupDraft } from "@/domains/billing/buildingSetupDraft";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBeforeUnload, unstable_usePrompt as usePrompt, useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useInView } from "react-intersection-observer";
import {
  createBuildingSchema,
  type BuildingCreationValues,
} from "@/domains/buildings/components/form/buildingCreationSchema";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { useBuildingCostConsent } from "@/domains/billing/useBuildingCostConsent";
import { bindTrackToCurrentUser } from "@/lib/posthog";
import { usePermissions } from "@/hooks/usePermissions";
import { addBillingBuilding, requestBillingSync } from "@/domains/billing/repository";
import {
  billingBlockMessage,
  billingErrorMessage,
  billingGuardMessage,
} from "@/domains/billing/messages";
import { PERMISSIONS } from "@/constants/permissions";
import { capture } from "@/lib/sentry/capture";
import type { BuildingCreationPlanConsent } from "@/domains/billing/useBuildingCreationPlan";
import { hasLegacyBillingAccess, useBillingStatus } from "@/domains/billing/BillingStatusContext";

export type BuildingFormValues = BuildingCreationValues;

// Warn before losing unsaved edits — on browser unload and on in-app route
// changes. usePrompt is guarded because non-Data routers don't support it;
// useBeforeUnload is the fallback in that case.
function useUnsavedChangesGuard(hasUnsavedChanges: boolean, message: string) {
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = "";
        }
      },
      [hasUnsavedChanges]
    )
  );

  try {
    usePrompt({ when: hasUnsavedChanges, message });
  } catch {
    // noop: fallback is handled by useBeforeUnload above
  }
}

interface UseBuildingFormOptions {
  /** Render the form immediately instead of deferring until it scrolls into
   * view. Set when the form is mounted inside a modal, where it is always
   * visible and the lazy in-view gate would only cause a flash. */
  immediate?: boolean;
  /** Called after a building is created successfully, before navigation — lets
   * a hosting modal close itself. */
  onSuccess?: () => void;
  /** The inline owner quote; submitting the form explicitly agrees to it. */
  creationPlan?: BuildingCreationPlanConsent;
}

export function useBuildingForm({
  immediate = false,
  onSuccess,
  creationPlan,
}: UseBuildingFormOptions = {}) {
  const navigate = useNavigate();
  const userId = useContext(AuthContext)?.userId;
  const { t } = useLanguage();
  const { currentOrganization } = useOrganizationContext();
  const buildingStore = useBuildingStore();
  const permissions = usePermissions();
  const billing = useBillingStatus();
  const requiresPlan = permissions.isOrgOwner && !hasLegacyBillingAccess(billing);
  const canCreate = permissions.canCreateBuildings || permissions.canChooseBuildingPlan;
  const createBlockedReason = !canCreate
    ? billingBlockMessage(permissions.billingBlockFor(PERMISSIONS.CREATE_BUILDINGS), t)
    : undefined;
  const { request: requestCostConsent, dialog: costConsentDialog } = useBuildingCostConsent();
  const [isFormReady, setIsFormReady] = useState(immediate);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const creationRequest = useRef<{ signature: string; id: string } | null>(null);

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const formSchema = useMemo(() => createBuildingSchema(t), [t]);

  useEffect(() => {
    if (inView && !isFormReady) {
      setIsFormReady(true);
    }
  }, [inView, isFormReady]);

  const form = useForm<BuildingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      address: "",
      registry_code: "",
    },
  });

  useEffect(() => {
    const subscription = form.watch(() => {
      setHasUnsavedChanges(form.formState.isDirty);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useUnsavedChangesGuard(hasUnsavedChanges, t.common.unsavedChangesPrompt);

  const onSubmit = useCallback(
    async (values: BuildingFormValues) => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);

        if (!currentOrganization) {
          toast.error("Please select an organisation before creating a building");
          return;
        }
        if (!canCreate) {
          toast.error(createBlockedReason ?? t.common.permissions.createBuildings);
          return;
        }
        const trackAction = bindTrackToCurrentUser(currentOrganization.id);

        if (
          requiresPlan &&
          (!creationPlan?.canSubmit || creationPlan.orgId !== currentOrganization.id)
        ) {
          toast.error(creationPlan?.reason ?? t.billing.consent.quoteUnavailable);
          return;
        }
        const tier = requiresPlan ? creationPlan?.tier : null;
        if (!requiresPlan && !(await requestCostConsent({ mode: "add" }))) return;

        const registryCode = values.registry_code?.trim() || null;
        const signature = JSON.stringify({
          orgId: currentOrganization.id,
          name: values.name,
          address: values.address,
          registryCode,
          tier,
        });
        if (tier && creationRequest.current?.signature !== signature)
          creationRequest.current = { signature, id: crypto.randomUUID() };

        const newBuilding = tier
          ? await addBillingBuilding(currentOrganization.id, {
              name: values.name,
              address: values.address,
              registryCode,
              tier,
              creationRequestId: creationRequest.current?.id,
            }).then((result) => ({
              id: String(result.building_id),
              activationPending: result.activation_pending || result.status === "pending",
            }))
          : await buildingStore.createBuilding({
              name: values.name,
              address: values.address,
              cadastral_code: "",
              registry_code: registryCode,
              orgId: currentOrganization.id,
            });
        if (tier) requestBillingSync(currentOrganization.id);
        creationRequest.current = null;

        trackAction("building_created", {});
        const activationPending =
          "activationPending" in newBuilding && newBuilding.activationPending;
        if (activationPending) toast.info(t.billing.plans.activationPending);
        else toast.success(t.buildings.form.success || "Building created successfully");

        try {
          if (!activationPending)
            await buildingStore.initializeBuildingData(Number(newBuilding.id));
        } catch (err) {
          capture(err instanceof Error ? err : new Error("Building initialization failed"), {
            tags: { feature: "building", stage: "initialize" },
            fingerprint: ["building", "initialize"],
            context: { building: { buildingId: newBuilding.id } },
          });
        }

        clearBuildingSetupDraft(userId, currentOrganization.id);
        form.reset();
        setHasUnsavedChanges(false);

        onSuccess?.();
        navigate(activationPending ? "/billing" : `/buildings/${newBuilding.id}`);
      } catch (error) {
        capture(error instanceof Error ? error : new Error("Building creation failed"), {
          tags: { feature: "building", stage: "create" },
          fingerprint: ["building", "create"],
          context: { building: { orgId: currentOrganization?.id } },
        });
        toast.error(
          billingGuardMessage(error, t) ?? billingErrorMessage(error, t, t.buildings.form.error)
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      userId,
      currentOrganization,
      buildingStore,
      navigate,
      t,
      form,
      onSuccess,
      requestCostConsent,
      creationPlan,
      requiresPlan,
      canCreate,
      createBlockedReason,
    ]
  );

  return {
    form,
    onSubmit,
    isSubmitting,
    currentOrganization,
    canCreate,
    createBlockedReason,
    isFormReady,
    ref,
    t,
    /** Legacy manager consent; owner creation agrees to the inline quote. */
    costConsentDialog,
  };
}

export default useBuildingForm;
